// Styles shared by the settings pages and dialogs of the suite (tags, permissions, calendar sync,
// calendar settings). Colours come from the Teams theme so every surface stays readable in dark and
// high-contrast Teams; the fallbacks are the light Teams values, for a tree rendered without the
// Teams theme (tests).
const colorScheme = (theme) => theme.siteVariables?.colorScheme;
const border = (theme) => `1px solid ${colorScheme(theme)?.default?.border2 ?? "#e0e0e0"}`;
export const mutedTextStyles = ({ theme }) => ({ color: colorScheme(theme)?.default?.foreground1 ?? "#605E5C" });
export const sectionTitleStyles = ({ theme }) => ({
    color: colorScheme(theme)?.default?.foreground1 ?? "#605E5C",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
});
export const headerIconStyles = ({ theme }) => ({
    width: "36px",
    height: "36px",
    minWidth: "36px",
    borderRadius: "8px",
    background: colorScheme(theme)?.brand?.background1 ?? "#e8ebfa",
    color: colorScheme(theme)?.brand?.foreground1 ?? "#5b5fc7",
    "& svg": { color: colorScheme(theme)?.brand?.foreground1 ?? "#5b5fc7" },
});
/** A bordered group of rows, separated by hairlines. */
export const listStyles = ({ theme }) => ({
    border: border(theme),
    borderRadius: "8px",
    overflow: "hidden",
    "& > *:not(:last-child)": { borderBottom: border(theme) },
});
export const listRowStyles = { padding: "0.625rem 0.75rem", minHeight: "44px" };
/** A bordered surface holding one item or one form. */
export const cardStyles = ({ theme }) => ({
    border: border(theme),
    borderRadius: "8px",
    padding: "0.875rem 1rem",
    background: colorScheme(theme)?.default?.background ?? "#ffffff",
});
/** A choice drawn as a card: the selected one takes the brand colour. */
export const optionCardStyles = (selected, disabled) => ({ theme }) => ({
    border: selected ? `2px solid ${colorScheme(theme)?.brand?.foreground ?? "#5b5fc7"}` : border(theme),
    // The border grows by 1px when selected: the padding shrinks as much, so nothing moves.
    padding: selected ? "calc(0.75rem - 1px) calc(0.875rem - 1px)" : "0.75rem 0.875rem",
    borderRadius: "8px",
    background: selected ? colorScheme(theme)?.brand?.background1 ?? "#e8ebfa" : colorScheme(theme)?.default?.background ?? "#ffffff",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled && !selected ? 0.6 : 1,
    ":hover": disabled || selected ? {} : { borderColor: colorScheme(theme)?.brand?.foreground ?? "#5b5fc7" },
});
export const premiumPillStyles = ({ theme }) => ({
    padding: "0 0.5rem",
    borderRadius: "999px",
    lineHeight: "20px",
    background: colorScheme(theme)?.brand?.background1 ?? "#e8ebfa",
    color: colorScheme(theme)?.brand?.foreground1 ?? "#5b5fc7",
    "& svg": { color: colorScheme(theme)?.brand?.foreground1 ?? "#5b5fc7" },
});
/** A small rounded label: a state, a licence, a role. */
export const statusPillStyles = (tone) => ({ theme }) => {
    const scheme = colorScheme(theme);
    const tones = {
        neutral: { background: scheme?.default?.background2 ?? "#f5f5f5", color: scheme?.default?.foreground1 ?? "#605E5C" },
        success: { background: scheme?.green?.background1 ?? "#e7f2da", color: scheme?.green?.foreground1 ?? "#237b4b" },
        warning: { background: scheme?.yellow?.background1 ?? "#fbf6d9", color: scheme?.yellow?.foreground2 ?? "#8a5d00" },
        brand: { background: scheme?.brand?.background1 ?? "#e8ebfa", color: scheme?.brand?.foreground1 ?? "#5b5fc7" },
    };
    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
        padding: "0 0.5rem",
        borderRadius: "999px",
        lineHeight: "20px",
        whiteSpace: "nowrap",
        ...tones[tone],
        "& svg": { color: tones[tone].color },
    };
};
export const premiumCardStyles = ({ theme }) => ({
    padding: "0.75rem",
    borderRadius: "8px",
    background: colorScheme(theme)?.brand?.background1 ?? "#e8ebfa",
});
export const dangerTextStyles = ({ theme }) => ({ color: colorScheme(theme)?.red?.foreground ?? "#c4314b" });
export const emptyStateStyles = ({ theme }) => ({
    padding: "1.5rem 1rem",
    border: `1px dashed ${colorScheme(theme)?.default?.border2 ?? "#e0e0e0"}`,
    borderRadius: "8px",
});
/** Northstar has no destructive button: a primary button in the theme's red. */
export const dangerButtonStyles = ({ theme }) => ({
    background: colorScheme(theme)?.red?.background ?? "#c4314b",
    borderColor: "transparent",
    color: colorScheme(theme)?.red?.foreground1 ?? "#ffffff",
    ":hover": { background: colorScheme(theme)?.red?.backgroundHover ?? "#a72037", color: colorScheme(theme)?.red?.foreground1 ?? "#ffffff" },
    ":active": { background: colorScheme(theme)?.red?.backgroundPressed ?? "#8e192e" },
});
/** The upgrade button beside the offer text: never squeezed into an ellipsis. */
export const premiumCtaStyles = { flexShrink: 0, maxWidth: "none" };
/** The bar pinned under a page while it holds unsaved changes. */
export const saveBarStyles = ({ theme }) => ({
    borderTop: border(theme),
    background: colorScheme(theme)?.default?.background ?? "#ffffff",
    boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
});
/** The readable column of a settings page. */
export const pageColumnStyles = (isOnMobile) => ({
    width: "100%",
    maxWidth: "880px",
    boxSizing: "border-box",
    padding: isOnMobile ? "0 0.75rem 1rem" : "0.5rem 1rem 1.5rem",
});
